import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ShareLoadButtons } from '../components/SocialButtons';
import { Truck, MapPin, Package, Calendar, MessageSquare, Send, ArrowLeft } from 'lucide-react';
import { formatDateAU, formatDateShortAU } from '../utils/dateFormat';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LoadDetails = () => {
  const { loadId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const highlightQid = searchParams.get('qid');
  const { toast } = useToast();
  const [load, setLoad] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactDetails, setContactDetails] = useState(null);
  const [showContactDetails, setShowContactDetails] = useState(false);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [checkingSubscription, setCheckingSubscription] = useState(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  
  // Q&A state
  const [questions, setQuestions] = useState([]);
  const [showQuestionForm, setShowQuestionForm] = useState(false);
  const [questionText, setQuestionText] = useState('');
  const [questionerEmail, setQuestionerEmail] = useState('');
  const [questionerName, setQuestionerName] = useState('');
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyingToQuestion, setReplyingToQuestion] = useState(null);
  const [submittingReply, setSubmittingReply] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const mapRef = React.useRef(null);
  const [quoteForm, setQuoteForm] = useState({
    carrier_email: '',
    amount: '',
    estimated_delivery_days: '',
    message: '',
    availability_start: '',
    availability_end: '',
    demurrage_rate: '',
    quote_valid_days: '7'
  });

  // Load Google Maps script
  React.useEffect(() => {
    // Use the shared loader to prevent duplicate Google Maps script tags
    import('../utils/googleMapsLoader').then(({ loadGoogleMaps }) => {
      loadGoogleMaps()
        .then(() => {
          console.log('Google Maps loaded successfully');
          setMapLoaded(true);
        })
        .catch((error) => {
          console.error('Failed to load Google Maps:', error);
        });
    });
  }, []);

  // Initialize map when load data and map script are ready
  React.useEffect(() => {
    if (mapLoaded && load && mapRef.current) {
      initializeMap();
    }
  }, [mapLoaded, load]);

  useEffect(() => {
    fetchLoadDetails();
  }, [loadId]);

  // Scroll to and highlight the target question when arriving from an email link
  useEffect(() => {
    if (!highlightQid || questions.length === 0) return;
    // Wait for the question card to render, then scroll & flash
    const timer = setTimeout(() => {
      const el = document.getElementById(`qa-${highlightQid}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('qa-highlight-flash');
        setTimeout(() => el.classList.remove('qa-highlight-flash'), 4000);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [highlightQid, questions]);

  const fetchLoadDetails = async () => {
    try {
      // Check if user is a carrier
      const carrierEmail = localStorage.getItem('user_email');
      const userRole = localStorage.getItem('user_role');
      
      // Add carrier_email param if user is a carrier to get shipper contact on paid loads
      let loadUrl = `${API}/loads/${loadId}`;
      if (carrierEmail && (userRole === 'carrier' || userRole === 'both')) {
        loadUrl += `?carrier_email=${encodeURIComponent(carrierEmail)}`;
      }
      
      const [loadRes, quotesRes, questionsRes] = await Promise.all([
        axios.get(loadUrl),
        axios.get(`${API}/quotes/load/${loadId}`),
        axios.get(`${API}/loads/${loadId}/questions`)
      ]);
      
      setLoad(loadRes.data);
      setQuotes(quotesRes.data);
      setQuestions(questionsRes.data.questions || []);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load details',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptQuote = async (quoteId) => {
    try {
      const response = await axios.put(`${API}/quotes/${quoteId}/accept`);
      
      // Show contact details after acceptance
      setContactDetails(response.data);
      setShowContactDetails(true);
      
      alert('Quote Accepted! Contact details are now revealed to both parties.');
      
      // Refresh data
      fetchLoadDetails();
    } catch (error) {
      alert('Failed to accept quote');
    }
  };

  const handleQuoteFormChange = (e) => {
    const { name, value } = e.target;
    setQuoteForm(prev => ({ ...prev, [name]: value }));
    
    // Check subscription when carrier email changes
    if (name === 'carrier_email' && value.includes('@')) {
      checkSubscription(value);
    }
  };

  const checkSubscription = async (email) => {
    setCheckingSubscription(true);
    try {
      const response = await axios.get(`${API}/carrier/subscription/${email}`);
      setSubscriptionStatus(response.data);
    } catch (error) {
      setSubscriptionStatus(null);
    } finally {
      setCheckingSubscription(false);
    }
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();
    
    if (!quoteForm.carrier_email || !quoteForm.amount || !quoteForm.estimated_delivery_days) {
      alert('Please fill in all required fields');
      return;
    }

    if (!quoteForm.gst_confirmed) {
      alert('Please confirm that your quote includes GST and fuel levy.');
      return;
    }

    // Check subscription status - warn but let backend do final validation for edge cases
    if (!subscriptionStatus) {
      alert('Please enter your carrier email above so we can verify your subscription status.');
      return;
    }
    if (!subscriptionStatus.is_active && !subscriptionStatus.approved_carrier) {
      alert('Active subscription required to submit quotes. Please subscribe or renew your subscription.');
      return;
    }

    setSubmittingQuote(true);
    try {
      await axios.post(`${API}/quotes`, {
        load_id: loadId,
        carrier_email: quoteForm.carrier_email,
        amount: parseFloat(quoteForm.amount),
        estimated_delivery_days: parseInt(quoteForm.estimated_delivery_days),
        message: quoteForm.message,
        availability_start: quoteForm.availability_start || null,
        availability_end: quoteForm.availability_end || null,
        demurrage_rate: quoteForm.demurrage_rate ? parseFloat(quoteForm.demurrage_rate) : null,
        quote_valid_days: parseInt(quoteForm.quote_valid_days)
      });
      
      alert('Quote submitted successfully! It will be reviewed by admin before being shown to the shipper.');
      setShowQuoteForm(false);
      setQuoteForm({
        carrier_email: '',
        amount: '',
        estimated_delivery_days: '',
        message: '',
        availability_start: '',
        availability_end: '',
        demurrage_rate: '',
        quote_valid_days: '7',
        gst_confirmed: false
      });
      setSubscriptionStatus(null);
      fetchLoadDetails();
    } catch (error) {
      let errorMessage = 'Failed to submit quote. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.status) {
        errorMessage = `Server error (${error.response.status}). Please contact admin at 1300 380 700.`;
      } else if (error.message) {
        errorMessage = `Network error: ${error.message}. Please check your connection and try again.`;
      }
      alert(errorMessage);
      console.error('Quote submission error:', error.response?.data || error.message || error);
    } finally {
      setSubmittingQuote(false);
    }
  };
  const initializeMap = () => {
    if (!window.google || !mapRef.current) {
      console.log('Map initialization skipped - google or ref not ready');
      return;
    }

    console.log('Initializing map for:', load.pickup_location, '->', load.dropoff_location);

    // Create map centered on Australia
    const map = new window.google.maps.Map(mapRef.current, {
      zoom: 5,
      center: { lat: -25.2744, lng: 133.7751 }, // Center of Australia
      mapTypeId: 'roadmap',
      mapTypeControl: true,
      streetViewControl: false,
      fullscreenControl: true
    });

    const geocoder = new window.google.maps.Geocoder();
    const bounds = new window.google.maps.LatLngBounds();
    let markersAdded = 0;

    // Geocode pickup location
    const pickupAddress = `${load.pickup_location}, Australia`;
    geocoder.geocode({ address: pickupAddress }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const pickupLocation = results[0].geometry.location;
        console.log('Pickup geocoded:', pickupAddress, pickupLocation.toString());
        
        // Create pickup marker
        new window.google.maps.Marker({
          position: pickupLocation,
          map: map,
          title: 'Pickup: ' + load.pickup_location,
          label: {
            text: 'P',
            color: 'white',
            fontWeight: 'bold'
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: '#22c55e',
            fillOpacity: 1,
            strokeColor: 'white',
            strokeWeight: 3,
            scale: 15
          }
        });

        bounds.extend(pickupLocation);
        markersAdded++;
        
        if (markersAdded === 2) {
          map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
        }
      } else {
        console.error('Pickup geocoding failed:', status, pickupAddress);
      }
    });

    // Geocode dropoff location
    const dropoffAddress = `${load.dropoff_location}, Australia`;
    geocoder.geocode({ address: dropoffAddress }, (results, status) => {
      if (status === 'OK' && results[0]) {
        const dropoffLocation = results[0].geometry.location;
        console.log('Dropoff geocoded:', dropoffAddress, dropoffLocation.toString());
        
        // Create dropoff marker
        new window.google.maps.Marker({
          position: dropoffLocation,
          map: map,
          title: 'Dropoff: ' + load.dropoff_location,
          label: {
            text: 'D',
            color: 'white',
            fontWeight: 'bold'
          },
          icon: {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: '#dc2626',
            fillOpacity: 1,
            strokeColor: 'white',
            strokeWeight: 3,
            scale: 15
          }
        });

        bounds.extend(dropoffLocation);
        markersAdded++;
        
        if (markersAdded === 2) {
          map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
        }
      } else {
        console.error('Dropoff geocoding failed:', status, dropoffAddress);
      }
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p>Loading...</p>
        </div>
        <Footer />
      </div>
    );
  }

  if (!load) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-4 py-12 text-center">
          <p>Load not found</p>
          <Button onClick={() => navigate('/')} className="mt-4">Go Home</Button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="container mx-auto px-4 sm:px-6 py-12">
        <div className="max-w-4xl mx-auto">
          <button
            onClick={() => navigate('/browse-loads')}
            className="flex items-center gap-1.5 text-[#1e3a5f] hover:text-blue-800 font-medium mb-4 transition-colors"
            data-testid="back-to-loads-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Loads
          </button>
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 mb-6">
            <div className="flex justify-between items-start mb-4">
              <h1 className="text-2xl sm:text-3xl font-bold text-[#1e3a5f]">Load Details</h1>
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${
                load.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                load.status === 'quoted' ? 'bg-blue-100 text-blue-800' :
                load.status === 'accepted' ? 'bg-green-100 text-green-800' :
                'bg-gray-100 text-gray-800'
              }`}>
                {load.status.toUpperCase()}
              </span>
            </div>
            
            <div className="mb-6">
              <ShareLoadButtons load={load} />
            </div>

            {/* Privacy Notice - show until shipper payment completed */}
            {!(load.payment_completed && (load.status === 'paid' || load.status === 'PAID')) && (
              <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Privacy Protection:</strong> Contact details are hidden until the shipper completes payment. 
                  This protects both shippers and carriers from being contacted outside the platform. 
                  If you require more information, please contact the Truckn Send It team to obtain this for you.
                </p>
              </div>
            )}

            {/* Shipper Contact Details (shown to carrier ONLY after shipper payment) */}
            {load.shipper_contact && load.payment_completed && (load.status === 'paid' || load.status === 'PAID') && (
              <div className="mb-6 p-6 bg-green-50 border-2 border-green-300 rounded-lg">
                <h3 className="text-lg font-bold text-green-800 mb-4">Shipper Contact Details</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600"><strong>Name:</strong> {load.shipper_contact.name}</p>
                    <p className="text-sm text-gray-600"><strong>Email:</strong> {load.shipper_contact.email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600"><strong>Phone:</strong> {load.shipper_contact.phone}</p>
                    {load.shipper_contact.company_name && (
                      <p className="text-sm text-gray-600"><strong>Company:</strong> {load.shipper_contact.company_name}</p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-green-700 mt-3">
                  Payment has been completed. You may now contact the shipper directly to arrange pickup.
                </p>
              </div>
            )}

            {/* Contact Details (shown after acceptance - legacy) */}
            {showContactDetails && contactDetails && (
              <div className="mb-6 p-6 bg-green-50 border-2 border-green-300 rounded-lg">
                <h3 className="text-lg font-bold text-green-800 mb-4">Contact Details Revealed</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Shipper Contact:</h4>
                    <p className="text-sm text-gray-600">Email: {contactDetails.shipper.email}</p>
                    <p className="text-sm text-gray-600">Phone: {contactDetails.shipper.phone}</p>
                    {contactDetails.shipper.company && (
                      <p className="text-sm text-gray-600">Company: {contactDetails.shipper.company}</p>
                    )}
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-2">Carrier Contact:</h4>
                    <p className="text-sm text-gray-600">Email: {contactDetails.carrier.email}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-red-600 mt-1" />
                <div>
                  <p className="font-semibold text-gray-700">Route</p>
                  <p className="text-gray-600">{load.pickup_location} → {load.dropoff_location}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <Package className="w-5 h-5 text-red-600 mt-1" />
                <div>
                  <p className="font-semibold text-gray-700">Cargo</p>
                  <p className="text-gray-600">{load.cargo_type}</p>
                  {load.cargo_description && <p className="text-sm text-gray-500 mt-1">{load.cargo_description}</p>}
                </div>
              </div>

              {/* Load Details Grid */}
              <div className="grid md:grid-cols-2 gap-4 pt-3 border-t">
                {load.weight && (
                  <div>
                    <p className="font-semibold text-gray-700 text-sm">Weight</p>
                    <p className="text-gray-600">{load.weight} kg</p>
                  </div>
                )}
                
                {load.dimensions && (
                  <div>
                    <p className="font-semibold text-gray-700 text-sm">Dimensions</p>
                    <p className="text-gray-600">{load.dimensions}</p>
                  </div>
                )}

                {Array.isArray(load.pallets) && load.pallets.length > 0 && (
                  <div className="md:col-span-2" data-testid="pallet-breakdown">
                    <p className="font-semibold text-gray-700 text-sm mb-2">
                      Pallet Breakdown ({load.pallets.length} pallet{load.pallets.length === 1 ? '' : 's'})
                    </p>
                    <div className="overflow-x-auto">
                      <table className="min-w-full text-sm border border-gray-200 rounded">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">#</th>
                            <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">Weight</th>
                            <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">L</th>
                            <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">W</th>
                            <th className="px-3 py-1.5 text-left text-xs font-semibold text-gray-600">H</th>
                          </tr>
                        </thead>
                        <tbody>
                          {load.pallets.map((p, i) => (
                            <tr key={i} className="border-t border-gray-100">
                              <td className="px-3 py-1.5 text-gray-700 font-medium">{i + 1}</td>
                              <td className="px-3 py-1.5 text-gray-700">{p.weight} {p.weight_unit || ''}</td>
                              <td className="px-3 py-1.5 text-gray-700">{p.length} {p.dimension_unit || ''}</td>
                              <td className="px-3 py-1.5 text-gray-700">{p.width} {p.dimension_unit || ''}</td>
                              <td className="px-3 py-1.5 text-gray-700">{p.height} {p.dimension_unit || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {load.oversize_ground_contact_dimensions && (
                  <div>
                    <p className="font-semibold text-gray-700 text-sm">Ground Contact Dimensions (Oversize)</p>
                    <p className="text-gray-600">{load.oversize_ground_contact_dimensions}</p>
                  </div>
                )}

                {load.oversize_extremity_dimensions && (
                  <div>
                    <p className="font-semibold text-gray-700 text-sm">Outermost Extremity Dimensions (Oversize)</p>
                    <p className="text-gray-600">{load.oversize_extremity_dimensions}</p>
                  </div>
                )}

                {load.cargo_value && (
                  <div>
                    <p className="font-semibold text-gray-700 text-sm">Cargo Value</p>
                    <p className="text-gray-600">${load.cargo_value.toLocaleString()}</p>
                  </div>
                )}

                {load.pickup_date && (
                  <div>
                    <p className="font-semibold text-gray-700 text-sm">Available From</p>
                    <p className="text-gray-600">{formatDateAU(load.pickup_date)}</p>
                  </div>
                )}

                {load.delivery_date && (
                  <div>
                    <p className="font-semibold text-gray-700 text-sm">Delivery Date</p>
                    <p className="text-gray-600">{formatDateAU(load.delivery_date)}</p>
                  </div>
                )}

                {load.pickup_time_window && (
                  <div>
                    <p className="font-semibold text-gray-700 text-sm">Pickup Time Window</p>
                    <p className="text-gray-600">{load.pickup_time_window}</p>
                  </div>
                )}

                {load.delivery_time_window && (
                  <div>
                    <p className="font-semibold text-gray-700 text-sm">Delivery Time Window</p>
                    <p className="text-gray-600">{load.delivery_time_window}</p>
                  </div>
                )}

                {load.preferred_carrier_type && (
                  <div>
                    <p className="font-semibold text-gray-700 text-sm">Preferred Carrier Type</p>
                    <p className="text-gray-600 capitalize">{load.preferred_carrier_type.replace(/_/g, ' ')}</p>
                  </div>
                )}
              </div>

              {/* Additional Requirements Section */}
              {(load.insurance_requirements || load.loading_equipment_needed || 
                load.unloading_equipment_needed || load.special_requirements) && (
                <div className="pt-4 border-t space-y-3">
                  <h3 className="font-semibold text-gray-700">Additional Requirements</h3>
                  
                  {load.insurance_requirements && (
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Insurance Requirements:</p>
                      <p className="text-sm text-gray-600">{load.insurance_requirements}</p>
                    </div>
                  )}
                  
                  {load.loading_equipment_needed && (
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Loading Equipment Needed:</p>
                      <p className="text-sm text-gray-600">{load.loading_equipment_needed}</p>
                    </div>
                  )}
                  
                  {load.unloading_equipment_needed && (
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Unloading Equipment Needed:</p>
                      <p className="text-sm text-gray-600">{load.unloading_equipment_needed}</p>
                    </div>
                  )}
                  
                  {load.special_requirements && (
                    <div>
                      <p className="text-sm font-semibold text-gray-600">Special Requirements:</p>
                      <p className="text-sm text-gray-600">{load.special_requirements}</p>
                    </div>
                  )}
                </div>
              )}

              {/* Freight Image */}
              {load.freight_image && (
                <div className="pt-4 border-t">
                  <p className="font-semibold text-gray-700 mb-3">Freight Image</p>
                  <p className="text-xs text-gray-500 mb-2">Tap image to enlarge</p>
                  <img 
                    src={load.freight_image} 
                    alt="Freight" 
                    className="w-full max-w-xs rounded-lg border cursor-pointer hover:opacity-80 object-contain"
                    style={{ maxHeight: '200px' }}
                    onClick={() => setLightboxImage(load.freight_image)}
                    data-testid="freight-image-thumb"
                  />
                </div>
              )}

              {/* Load Photos (uploaded by admin) */}
              {load.load_photos && load.load_photos.length > 0 && (
                <div className="pt-4 border-t">
                  <p className="font-semibold text-gray-700 mb-3">Load Photos ({load.load_photos.length})</p>
                  <p className="text-xs text-gray-500 mb-2">Tap image to enlarge</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {load.load_photos.map((photo, idx) => (
                      <img
                        key={idx}
                        src={photo}
                        alt={`Load photo ${idx + 1}`}
                        className="w-full h-32 object-cover rounded-lg border cursor-pointer hover:opacity-80"
                        onClick={() => setLightboxImage(photo)}
                        data-testid={`load-photo-${idx}`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Route Map Section */}
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 mb-6">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-4">Route Map</h2>
            {mapLoaded ? (
              <div 
                ref={mapRef}
                className="w-full h-96 rounded-lg border border-gray-200"
                style={{ minHeight: '400px' }}
              />
            ) : (
              <div className="w-full h-96 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                <p className="text-gray-500">Loading map...</p>
              </div>
            )}
            <div className="mt-4 flex items-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">P</span>
                </div>
                <span>Pickup Location</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs font-bold">D</span>
                </div>
                <span>Dropoff Location</span>
              </div>
            </div>
          </div>

          {/* Carrier Quote Submission Section */}
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-[#1e3a5f]">Submit a Quote (Carriers)</h2>
              {!showQuoteForm && (
                <Button
                  onClick={() => setShowQuoteForm(true)}
                  className="bg-red-600 hover:bg-red-700 text-white"
                >
                  Submit Your Quote
                </Button>
              )}
            </div>

            {showQuoteForm && (
              <form onSubmit={handleSubmitQuote} className="space-y-4 border-t pt-4">
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Your quote will be reviewed by admin before being shown to the shipper. 
                    The shipper will see your exact quote amount. You must have an active subscription or be an adhoc member to submit quotes.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="carrier_email">Your Email *</Label>
                    <Input
                      id="carrier_email"
                      name="carrier_email"
                      type="email"
                      value={quoteForm.carrier_email}
                      onChange={handleQuoteFormChange}
                      required
                      placeholder="carrier@example.com"
                    />
                    {checkingSubscription && (
                      <p className="text-xs text-gray-500 mt-1">Checking subscription...</p>
                    )}
                    {subscriptionStatus && (
                      <div className={`mt-2 p-2 rounded text-xs ${
                        subscriptionStatus.is_active 
                          ? 'bg-green-50 text-green-800' 
                          : subscriptionStatus.approved_carrier
                            ? 'bg-yellow-50 text-yellow-800'
                            : 'bg-red-50 text-red-800'
                      }`}>
                        {subscriptionStatus.is_active ? (
                          <>
                            ✓ Active Subscription ({subscriptionStatus.subscription_plan || 'Active'})
                            {subscriptionStatus.subscription_expiry && (
                              <> - Expires: {formatDateAU(subscriptionStatus.subscription_expiry)}</>
                            )}
                          </>
                        ) : subscriptionStatus.approved_carrier ? (
                          <>
                            ⚠ Subscription expired or pending — Contact admin at 1300 380 700 or try submitting your quote (the system may auto-resolve).
                          </>
                        ) : (
                          <>
                            ✗ No Active Subscription - <a href="/signup?role=carrier" className="underline font-semibold">Subscribe Now</a>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="amount">Your Quote Amount ($) *</Label>
                    <Input
                      id="amount"
                      name="amount"
                      type="number"
                      step="0.01"
                      value={quoteForm.amount}
                      onChange={handleQuoteFormChange}
                      required
                      placeholder="1500.00"
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      Enter number only, no symbols (e.g. $). Please ensure your quote includes GST and fuel levy.
                    </p>
                  </div>

                  <div>
                    <Label htmlFor="estimated_delivery_days">Estimated Delivery (days) *</Label>
                    <Input
                      id="estimated_delivery_days"
                      name="estimated_delivery_days"
                      type="number"
                      value={quoteForm.estimated_delivery_days}
                      onChange={handleQuoteFormChange}
                      required
                      placeholder="3"
                    />
                  </div>
                </div>

                {/* Availability Window */}
                <div className="bg-gray-50 p-4 rounded-lg border">
                  <h3 className="font-semibold text-[#1e3a5f] mb-3">Your Availability Window</h3>
                  <p className="text-xs text-gray-600 mb-3">
                    Let the shipper know when you're available to pick up and transport this load
                  </p>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="availability_start">Available From</Label>
                      <Input
                        id="availability_start"
                        name="availability_start"
                        type="date"
                        value={quoteForm.availability_start}
                        onChange={handleQuoteFormChange}
                        data-testid="availability-start"
                      />
                    </div>
                    <div>
                      <Label htmlFor="availability_end">Available Until</Label>
                      <Input
                        id="availability_end"
                        name="availability_end"
                        type="date"
                        value={quoteForm.availability_end}
                        onChange={handleQuoteFormChange}
                        data-testid="availability-end"
                      />
                    </div>
                  </div>
                </div>

                {/* Demurrage Rate */}
                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                  <h3 className="font-semibold text-[#1e3a5f] mb-2">Demurrage Charge</h3>
                  <p className="text-xs text-gray-600 mb-2">
                    Your quote should allow 1 hour for loading and 1 hour for unloading. 
                    Demurrage is charged where loading or unloading exceeds one hour on each end.
                  </p>
                  <p className="text-xs text-gray-600 mb-3 font-semibold">
                    In the event of demurrage — fees charged due to, but not limited to: equipment/freight not being ready to load at the agreed-upon time, not being in the location specified on the load request, undisclosed access requirements, incorrect measurements or weights, or other incurred expenses (e.g. pilots and permits) — the shipper will be contacted by Truckn Send It to pay the demurrage fee before delivery.
                  </p>
                  <div>
                    <Label htmlFor="demurrage_rate">Hourly Demurrage Rate ($)</Label>
                    <Input
                      id="demurrage_rate"
                      name="demurrage_rate"
                      type="number"
                      step="0.01"
                      value={quoteForm.demurrage_rate}
                      onChange={handleQuoteFormChange}
                      placeholder="85.00"
                      data-testid="demurrage-rate"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message">Additional Message (Optional)</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={quoteForm.message}
                    onChange={handleQuoteFormChange}
                    placeholder="Any additional information about your quote..."
                    rows={3}
                  />
                </div>

                <div>
                  <Label htmlFor="quote_valid_days">Quote Valid For *</Label>
                  <select
                    id="quote_valid_days"
                    name="quote_valid_days"
                    value={quoteForm.quote_valid_days}
                    onChange={handleQuoteFormChange}
                    className="w-full h-10 border rounded-md px-3 text-sm"
                    data-testid="quote-valid-days-select"
                  >
                    <option value="1">1 Day</option>
                    <option value="3">3 Days</option>
                    <option value="7">7 Days</option>
                    <option value="14">14 Days</option>
                    <option value="21">21 Days</option>
                    <option value="30">30 Days</option>
                  </select>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={quoteForm.gst_confirmed || false}
                      onChange={(e) => setQuoteForm(prev => ({ ...prev, gst_confirmed: e.target.checked }))}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300"
                      data-testid="gst-fuel-confirm-checkbox"
                    />
                    <span className="text-sm text-blue-900">
                      I confirm that my quote includes GST and fuel levy.
                    </span>
                  </label>
                </div>

                <div className="flex gap-3">
                  <Button
                    type="submit"
                    disabled={submittingQuote}
                    className="bg-green-600 hover:bg-green-700 text-white flex-1"
                  >
                    {submittingQuote ? 'Submitting...' : 'Submit Quote'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowQuoteForm(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
          </div>

          {/* Q&A Section */}
          <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
            <h2 className="text-xl font-bold text-[#1e3a5f] mb-6 flex items-center gap-2">
              <MessageSquare className="w-6 h-6" />
              Questions & Answers
            </h2>
            
            {/* Ask a Question Button */}
            {!showQuestionForm ? (
              <Button
                onClick={() => setShowQuestionForm(true)}
                variant="outline"
                className="mb-6"
                data-testid="ask-question-btn"
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                Ask the Shipper a Question
              </Button>
            ) : (
              <Card className="p-4 mb-6 border-blue-200 bg-blue-50">
                <h3 className="font-semibold text-[#1e3a5f] mb-3">Ask a Question</h3>
                <p className="text-xs text-gray-600 mb-3">
                  Your question will be visible after admin approval.
                </p>
                <div className="space-y-3">
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <Label htmlFor="questionerName">Your Name *</Label>
                      <Input
                        id="questionerName"
                        value={questionerName}
                        onChange={(e) => setQuestionerName(e.target.value)}
                        placeholder="John Smith"
                        data-testid="questioner-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="questionerEmail">Your Email *</Label>
                      <Input
                        id="questionerEmail"
                        type="email"
                        value={questionerEmail}
                        onChange={(e) => setQuestionerEmail(e.target.value)}
                        placeholder="john@example.com"
                        data-testid="questioner-email"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="questionText">Your Question *</Label>
                    <Textarea
                      id="questionText"
                      value={questionText}
                      onChange={(e) => setQuestionText(e.target.value)}
                      placeholder="What would you like to know about this load?"
                      rows={3}
                      data-testid="question-text"
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={async () => {
                        if (!questionerEmail || !questionerName || !questionText) {
                          alert('Please fill in all fields');
                          return;
                        }
                        setSubmittingQuestion(true);
                        try {
                          await axios.post(`${API}/loads/${loadId}/questions`, {
                            asker_email: questionerEmail,
                            asker_name: questionerName,
                            question_text: questionText
                          });
                          alert('Question submitted! It will be visible after admin approval.');
                          setShowQuestionForm(false);
                          setQuestionText('');
                          setQuestionerEmail('');
                          setQuestionerName('');
                          fetchLoadDetails();
                        } catch (error) {
                          alert(error.response?.data?.detail || 'Failed to submit question');
                        } finally {
                          setSubmittingQuestion(false);
                        }
                      }}
                      disabled={submittingQuestion}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="submit-question-btn"
                    >
                      {submittingQuestion ? 'Submitting...' : 'Submit Question'}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowQuestionForm(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              </Card>
            )}
            
            {/* Questions List */}
            {questions.length === 0 ? (
              <p className="text-gray-500 text-center py-6">
                No questions yet. Be the first to ask!
              </p>
            ) : (
              <div className="space-y-4">
                {questions.map((question) => (
                  <Card
                    key={question.id}
                    id={`qa-${question.id}`}
                    data-testid={`qa-item-${question.id}`}
                    className={`p-4 border-l-4 border-l-blue-500 transition-all duration-500 ${
                      highlightQid === question.id ? 'ring-2 ring-yellow-400 shadow-lg' : ''
                    }`}
                  >
                    <div className="flex items-start gap-3 mb-2">
                      <MessageSquare className="w-5 h-5 text-blue-600 mt-0.5" />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800 mb-1">Q:</p>
                        <p className="text-gray-700">{question.question_text}</p>
                      </div>
                    </div>
                    
                    {/* Replies */}
                    {question.replies && question.replies.length > 0 && (
                      <div className="ml-8 mt-3 space-y-2">
                        {question.replies.map((reply) => (
                          <div key={reply.id} className="bg-green-50 p-3 rounded border-l-4 border-l-green-500">
                            <p className="font-semibold text-green-800 mb-1">A:</p>
                            <p className="text-gray-700">{reply.reply_text}</p>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Reply Form (for shipper only) */}
                    {load?.shipper_email && replyingToQuestion === question.id ? (
                      <div className="ml-8 mt-3">
                        <div className="bg-gray-50 p-3 rounded">
                          <Label>Your Reply</Label>
                          <Textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Type your reply..."
                            rows={2}
                            className="mb-2"
                          />
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              onClick={async () => {
                                if (!replyText.trim()) return;
                                setSubmittingReply(true);
                                try {
                                  await axios.post(`${API}/loads/${loadId}/questions/${question.id}/reply`, {
                                    replier_email: localStorage.getItem('user_email') || load.shipper_email,
                                    reply_text: replyText
                                  });
                                  alert('Reply submitted! It will be visible after admin approval.');
                                  setReplyingToQuestion(null);
                                  setReplyText('');
                                  fetchLoadDetails();
                                } catch (error) {
                                  alert(error.response?.data?.detail || 'Failed to submit reply');
                                } finally {
                                  setSubmittingReply(false);
                                }
                              }}
                              disabled={submittingReply}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              {submittingReply ? 'Sending...' : 'Send Reply'}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setReplyingToQuestion(null);
                                setReplyText('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : load?.shipper_email && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ml-8 mt-2 text-blue-600"
                        onClick={() => setReplyingToQuestion(question.id)}
                      >
                        <Send className="w-4 h-4 mr-1" />
                        Reply to this question
                      </Button>
                    )}
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <Footer />

      {/* Image Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-[60] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
          data-testid="image-lightbox"
        >
          <button
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full w-10 h-10 flex items-center justify-center text-2xl hover:bg-black/70 z-10"
            onClick={() => setLightboxImage(null)}
          >
            &times;
          </button>
          <img 
            src={lightboxImage} 
            alt="Enlarged view" 
            className="max-w-full max-h-[90vh] object-contain rounded"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default LoadDetails;